import { OrderedMap, Map, List, Iterable } from 'immutable'

function getValues(list) {
  return Iterable.isIterable(list) && 'function' == typeof list.values ? list.values() : list
}

const super_get = OrderedMap.prototype.get || Iterable.prototype.get
const super_set = OrderedMap.prototype.set || Map.prototype.set
const super_remove = OrderedMap.prototype.remove || Map.prototype.remove
const super_has = OrderedMap.prototype.has || Iterable.prototype.has

export default function ItemsOrderedMap(
  itemMapper = x => (Map.isMap(x) ? x : Map(x)),
  extendWith,
  itemsOrderedMapName,
  { transformList } = {}
) {
  const OrderedMapExtender = {
    toJS() {
      return this.toArray().map(i => (Iterable.isIterable(i) ? i.toJS() : i))
    },
    get(k, notSetValue) {
      if ('number' == typeof k) {
        const entry = this._list.filter(v => null != v).get(k)
        return entry ? entry[1] : notSetValue
      } else {
        return super_get.call(this, k, notSetValue)
      }
    },
    set(k, v) {
      if ('number' == typeof k && (k > this.size || k < -this.size)) {
        return this
      }
      const preMappedValue = itemMapper(v, 'number' == typeof k ? (k < 0 ? this.size + k : k) : this._map.get(k))
      if (null == preMappedValue) return this
      const mappedValue = preMappedValue.get('uid') ? preMappedValue : preMappedValue.set('uid', `${k}`)
      const mappedValueUID = mappedValue.get('uid')
      const key = 'number' == typeof k ? (this._list.filter(v => null != v).get(k) || [])[0] : k
      if (key == mappedValueUID) {
        return extendOrderedMap(super_set.call(this, key, mappedValue))
      }
      const updatedOrderedMapEntries = this.entrySeq()
        .map(([ek, ev]) =>
          null == key && ek == mappedValueUID ? null : ek == key ? [mappedValueUID, mappedValue] : [ek, ev]
        )
        .filter(x => null != x)
      return extendOrderedMap(
        OrderedMap(
          null == key || !this.has(key)
            ? updatedOrderedMapEntries.concat([[mappedValueUID, mappedValue]])
            : updatedOrderedMapEntries
        )
      )
    },
    remove(k) {
      if ('number' == typeof k) {
        const entry = this._list.filter(v => null != v).get(k)
        return entry ? extendOrderedMap(super_remove.call(this, entry[0])) : this
      } else {
        return extendOrderedMap(super_remove.call(this, k))
      }
    },
    has(k) {
      return 'number' == typeof k ? this._list.filter(v => null != v).has(k) : super_has.call(this, k)
    },
    insertAt(k, v) {
      if (null != k && (k > this.size || k < -this.size)) {
        return this
      }
      const preMappedValue = itemMapper(v, k)
      if (null == preMappedValue) return this
      const mappedValue = preMappedValue.get('uid') ? preMappedValue : preMappedValue.set('uid', `${k}`)
      const filteredList = this._list.filter(v => null != v)
      return extendOrderedMap(
        OrderedMap(
          null == k
            ? filteredList.concat([[mappedValue.get('uid'), mappedValue]])
            : filteredList.splice(k, 0, [mappedValue.get('uid'), mappedValue])
        )
      )
    },
    uidByIndex(index) {
      const entry = this._list.filter(v => null != v).get(index)
      return entry ? entry[0] : null
    },
    indexByUID(uid) {
      return this._list.filter(v => null != v).findIndex(([_k, v]) => uid == v.get('uid'))
    },
    indexOf(item) {
      return this._list.filter(v => null != v).findIndex(([_k, v]) => v == item)
    },
    findIndex(predicate) {
      return this._list.filter(v => null != v).findIndex((value, index, iter) => predicate(value[1], index, iter))
    },
    findLastIndex(predicate) {
      return this._list.filter(v => null != v).findLastIndex((value, index, iter) => predicate(value[1], index, iter))
    },
    clone(...cloneArgs) {
      return extendOrderedMap(
        OrderedMap(
          this.entrySeq().map(([_k, v], index) => {
            const mappedValue = itemMapper(
              null != v && 'object' == typeof v && 'function' == typeof v.clone ? v.clone(...cloneArgs) : v,
              index
            )
            return [mappedValue.get('uid'), mappedValue]
          })
        )
      )
    },
    isItemsOrderedMap: true
  }
  OrderedMapExtender.delete = OrderedMapExtender.remove

  const emptyItemsOrderedMap = extendOrderedMap(Object.assign(OrderedMap([['a', 'a']]), OrderedMap()))
  OrderedMapExtender.clear = function () {
    return emptyItemsOrderedMap
  }

  function extendOrderedMap(orderedMap) {
    if (orderedMap == OrderedMap()) return emptyItemsOrderedMap
    if (null != extendWith && 'object' == typeof extendWith) {
      for (const extendWithKey of Object.keys(extendWith)) {
        orderedMap[extendWithKey] = extendWith[extendWithKey]
      }
    }
    if (itemsOrderedMapName) {
      orderedMap[`is${itemsOrderedMapName}`] = true
    }
    return Object.assign(orderedMap, OrderedMapExtender)
  }

  const ItemsOrderedMap = function (list) {
    return extendOrderedMap(
      OrderedMap(
        ('function' == typeof transformList ? transformList(List(getValues(list))) : List(getValues(list))).map(
          (i, index) => {
            const item = itemMapper(i, index)
            return null == item ? null : [item.get('uid'), item]
          }
        )
      )
    )
  }

  ItemsOrderedMap.of = function (...list) {
    return ItemsOrderedMap(list)
  }
  ItemsOrderedMap.empty = function () {
    return ItemsOrderedMap.of()
  }
  ItemsOrderedMap.isItemsOrderedMap = function (target) {
    return OrderedMap.isOrderedMap(target) && target.isItemsOrderedMap
  }
  if (itemsOrderedMapName) {
    ItemsOrderedMap[`is${itemsOrderedMapName}`] = function (target) {
      return ItemsOrderedMap.isItemsOrderedMap(target) && target[`is${itemsOrderedMapName}`]
    }
  }

  return ItemsOrderedMap
}

ItemsOrderedMap.isItemsOrderedMap = function (target) {
  return OrderedMap.isOrderedMap(target) && target.isItemsOrderedMap
}
